<template>
  <li
    class="cart-slideout-item cart-item"
    :class="{ 'cart-item--updating': updatingItem }"
  >
    <a
      :href="item.url"
      class="cart-item__image"
      :class="{ 'cart-item__image--png': item.image.includes('.png') }"
    >
      <img
        :src="image"
        :alt="item.featured_image.alt"
        width="70"
        height="70"
      >
    </a>
    <div class="cart-slideout-item__text">
      <ul class="cart-item__information">
        <li>
          <h3 class="cart-item__title">
            {{ productTitle }}
          </h3>
        </li>
        <li class="cart-item__information-item cart-item__information-item--price">
          <s v-if="item.original_line_price != item.final_line_price">
            {{ originalLinePrice }}
          </s>
          {{ finalLinePrice }}
        </li>
        <li
          v-for="option in optionsWithValues"
          :key="`${item.id}-${option.name}`"
          class="cart-item__information-item"
        >
          {{ option.name }}: {{ option.value }}
        </li>
        <li
          v-if="isSubscriptionItem"
          class="cart-item__information-item"
        >
          Subscription: {{ subscriptionFrequency }}
        </li>
        <li
          v-if="isCustomisableBundle"
          class="cart-item__information-item cart-item__information-item--bundle-contents"
          v-html="bundleText"
        />
        <li
          v-if="isUpgradeable || isUpgraded"
          class="size-servings"
        >
          {{ tubSize }} / {{ servingsSize }}&nbsp;{{ productStrings.servings }}
        </li>
      </ul>
      <button
        class="cart-item__delete"
        @click="removeItemFromCart"
      >
        <DeleteIcon />
      </button>
      <div
        class="cart-item__quantity"
      >
        <button
          class="quantity-buttons__button"
          @click="decreaseQuantity"
        >
          <MinusCircleFilled />
        </button>
        <span class="quantity-buttons__amount">
          {{ item.quantity }}
        </span>
        <button
          class="quantity-buttons__button"
          :disabled="!customerCanIncreaseQuantity"
          @click="increaseQuantity"
        >
          <PlusCircleFilled />
        </button>
      </div>
    </div>
    <div
      v-if="isUpgradeable || isUpgraded"
      class="cart-item__upgrade"
      :class="{ 'upgraded': isUpgraded }"
    >
      <button
        class="upgrade"
        @click="upgradeAction"
      >
        <LightningYellowIcon />
        <span
          class="upgrade-text"
          v-html="upgradeText"
        />
      </button>
      <span
        v-if="!isUpgraded"
        class="upgrade-price"
      >{{ upgradePrice }}</span>
      <span class="upgrade-servings">
        <del>{{ baseServingPrice }}</del> <strong>{{ upgradeServingPrice }}</strong> <strong class="upgrade-servings-text">{{ perServingText }}</strong>
      </span>
    </div>
  </li>
</template>

<script>
import {useCartStore} from '../../pinia/CartStore'
import {mapActions, mapState} from 'pinia'

import {productPrice} from '../../helpers/product'
import {getSizedImageUrl} from '@shopify/theme-images'

import DeleteIcon from '../../icons/delete-icon'
import PlusCircleFilled from '../../icons/plus-circle-filled'
import MinusCircleFilled from '../../icons/minus-circle-filled'
import LightningYellowIcon from '../../icons/lightning-yellow'

export default {
  components: {
    DeleteIcon,
    PlusCircleFilled,
    MinusCircleFilled,
    LightningYellowIcon
  },
  props: {
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      strings: {...window.theme.cart.strings},
      productStrings: {...window.theme.strings.product},
      bundles: [...window.theme.bundles],
      purchaseLimitProducts: [...window.theme.cart.purchaseLimitCollectionProducts],
      updatingItem: false,
      upgradeTitle: window.theme.strings.product.upgradeProductTitle,
      baseTitle: window.theme.strings.product.baseProductTitle,
    }
  },
  computed: {
    ...mapState(useCartStore, [
      'bundleItems'
    ]),

    image() {
      return getSizedImageUrl(this.item.featured_image.url, '190x')
    },
    originalLinePrice() {
      return productPrice(this.item.original_line_price)
    },
    finalLinePrice() {
      return this.isCustomisableBundle ? productPrice(this.bundlePrice * this.item.quantity) : productPrice(this.item.final_line_price)
    },
    itemHasVariants() {
      return !this.item.product_has_only_default_variant && (this.item.options_with_values[0].name !== 'Options')
    },
    optionsWithValues() {
      return this.itemHasVariants ? {...this.item.options_with_values?.filter((option) => option.name !== 'Type')} : []
    },
    isSubscriptionItem() {
      return this.item.selling_plan_allocation
    },
    subscriptionFrequency() {
      return this.isSubscriptionItem ? this.item.selling_plan_allocation.selling_plan.options[0].value : null
    },
    productHasPurchaseLimit() {
      return !!this.purchaseLimitProducts.find(product => product.handle === this.item.handle)
    },
    purchaseLimit() {
      return this.productHasPurchaseLimit ? this.purchaseLimitProducts.find(product => product.handle === this.item.handle).purchaseLimit : 999999
    },
    customerHasReachedOrExceededQuantityLimit() {
      if (!this.productHasPurchaseLimit) return false
      return this.item.quantity >= this.purchaseLimit
    },
    customerHasExceededLimit() {
      if (!this.productHasPurchaseLimit) return false
      return this.item.quantity > this.purchaseLimit
    },
    customerCanIncreaseQuantity() {
      return !this.customerHasReachedOrExceededQuantityLimit
    },
    isCustomisableBundle() {
      return !!this.item.properties?._bundle_key || !!this.bundles.find(bundle => bundle.product.handle === this.item.handle)
    },
    isMetafieldsBundle() {
      return !!this.item.properties?._bundle_key
    },
    bundleText() {
      if (!this.isCustomisableBundle) {
        return null
      }
      let text = ''
      for (const [key, value] of Object.entries(this.item.properties)) {
        if (key.startsWith('_bundle')) {
          continue
        }
        if (!(typeof value === 'string' || value instanceof String)) {
          continue
        }
        const productTitle = value.replace(/in-game-/g, '')
          .replace(/-solo/g, '')
          .replace(/-sachet/g, '')
          .replace(/-shaker/g, '')
          .replace(/sneak-/g, '')
          .replace(/-7g/g, '')
          .replace(/-210g-uk/g, '')
          .replace(/-5-x-10g/g, '')
          .replace(/-/g, ' ')
          .replace(/og/g, 'OG')
        text = `${text}<span>${value.includes('5-x-10') ? '5 X ' : ''}${productTitle}${value.includes('5-x-10') ? '' : ` ${key.replace(/^_/, '').split(' ')[0].toLowerCase()}`}</span>`
      }
      return text
    },
    matchingBundleItems() {
      if (!this.isCustomisableBundle) return []
      return this.bundleItems?.filter(item => {
        return (item.properties.bundle === this.item.handle)
      })
    },
    matchingBundleItemsWithQuantites() {
      const items = []
      if (!this.isMetafieldsBundle) {
        for (const [key, value] of Object.entries(this.item.properties)) {
          const matchingCartItem = this.matchingBundleItems.find(item => item.handle === value)
          if (items.find(addedItem => addedItem.key === matchingCartItem.key)) {
            const indexOfItemInArray = items.findIndex(addedItem => addedItem.key === matchingCartItem.key)
            items[indexOfItemInArray].bundleQuantity = items[indexOfItemInArray].bundleQuantity + 1
          } else {
            items.push(
              {
                key: matchingCartItem.key,
                bundleQuantity: 1,
                quantity: matchingCartItem.quantity
              }
            )
          }
        }
      }
      return items
    },
    bundleRemovePayload() {
      const payload = {
        updates: {
          [this.item.key]: 0
        }
      }
      this.matchingBundleItemsWithQuantites.forEach(item => {
        payload.updates[item.key] = item.quantity - (this.item.quantity * item.bundleQuantity)
      })
      return payload
    },
    bundleIncreasePayload() {
      const payload = {
        updates: {
          [this.item.key]: this.item.quantity + 1
        }
      }
      this.matchingBundleItemsWithQuantites.forEach(item => {
        payload.updates[item.key] = item.quantity + item.bundleQuantity
      })
      return payload
    },
    bundleDecreasePayload() {
      const payload = {
        updates: {
          [this.item.key]: this.item.quantity - 1
        }
      }
      this.matchingBundleItemsWithQuantites.forEach(item => {
        payload.updates[item.key] = item.quantity - item.bundleQuantity
      })
      return payload
    },
    bundlePrice() {
      if (this.isMetafieldsBundle) {
        return this.item.final_price
      }

      if (!this.isCustomisableBundle) {
        return null
      }

      let price = 0.00
      for (const [key, value] of Object.entries(this.item.properties)) {
        const matchingCartItem = this.matchingBundleItems.find(item => item.handle === value)
        if (matchingCartItem !== undefined && matchingCartItem?.final_price) {
          price += matchingCartItem.final_price
        }
      }

      if (price === 0.00) {
        price = this.item.final_price
      }
      return price
    },
    productTitle() {
      return this.isUpgradeable ? this.item.product_title.replace(this.baseTitle, '') : this.item.product_title
    },
    isUpgradeable() {
      return typeof this.baseProduct === 'object' && typeof this.upgradeProduct === 'object' && this.item.variant_id === this.baseProduct.variants[0].id
    },
    isUpgraded() {
      return this.item.properties?.upgraded
    },
    upgradeText() {
      return this.isUpgraded ? this.productStrings.upgraded : this.productStrings.upgrade + `<span>(${this.upgradeTitle})</span>`
    },
    getFlavour() {
      return window.theme.flavourCollections.find(collection => collection.products.find(product => product.variants[0].id === this.item.variant_id))
    },
    baseProduct() {
      const item = this.getFlavour?.products.find(product => product.variants[0].id === this.item.variant_id)
      // if already upgraded get 150g from flavour
      if (item && this.upgradeTitle === item.title) {
        return this.getFlavour.products.find(product => product.title === this.baseTitle)
      }
      return item
    },
    upgradeProduct() {
      return this.getFlavour?.products.find(product => product.title === this.upgradeTitle)
    },
    upgradePrice() {
      if (this.isUpgraded) {
        return ''
      }
      return '+' + productPrice(this.upgradeProduct?.variants[0].price - this.item.price)
    },
    baseServingPrice() {
      return productPrice(this.baseProduct?.variants[0].price/(this.baseProduct?.servings || 1))
    },
    upgradeServingPrice() {
      return productPrice(this.upgradeProduct?.variants[0].price/(this.upgradeProduct?.servings || 1))
    },
    perServingText() {
      return this.productStrings.perServing
    },
    tubSize() {
      if (this.isUpgraded) {
        return this.upgradeProduct.weight
      }
      return this.isUpgradeable ? this.baseProduct.weight : this.upgradeProduct.weight
    },
    servingsSize() {
      if (this.isUpgraded) {
        return  this.upgradeProduct.servings
      }
      return this.isUpgradeable ? this.baseProduct.servings : this.upgradeProduct.servings
    }
  },
  watch: {
    item() {
      if (this.customerHasExceededLimit) this.decreaseQuantity()
    }
  },
  mounted() {
    if (this.customerHasExceededLimit) this.decreaseQuantity()
  },
  methods: {
    ...mapActions(useCartStore, [
      'addItems',
      'changeItems',
      'updateItems',
      'removeItem'
    ]),
    async increaseQuantity() {
      if (this.customerCanIncreaseQuantity) {
        this.updatingItem = true
        const newQuantity = this.item.quantity + 1
        const payload = {
          'id': this.item.key,
          'quantity': newQuantity
        }
        if (this.isCustomisableBundle) {
          await this.updateItems(this.bundleIncreasePayload)
        } else {
          await this.changeItems(payload)
        }
        this.updatingItem = false
      }
    },
    async decreaseQuantity() {
      this.updatingItem = true
      const newQuantity = this.item.quantity - 1
      const payload = {
        'id': this.item.key,
        'quantity': newQuantity
      }

      if (newQuantity === 0) {
        await this.removeItemFromCart()
      } else {
        if (this.isCustomisableBundle) {
          await this.updateItems(this.bundleDecreasePayload)
        } else {
          await this.changeItems(payload)
        }
      }
      this.updatingItem = false
    },
    async removeItemFromCart() {
      this.updatingItem = true

      if (this.isCustomisableBundle) {
        await this.updateItems(this.bundleRemovePayload)
      } else {
        await this.removeItem(this.item.key)
      }

      this.updatingItem = false
    },
    async upgradeAction() {
      if (this.isUpgraded) return
      this.updatingItem = true
      const qty = this.item.quantity
      await this.removeItem(this.item.key)
      await this.addItems({
        items: [
          {
            id: this.upgradeProduct.variants[0].id,
            quantity: qty,
            properties: {
              upgraded: true
            }
          }
        ]
      })
      this.updatingItem = false
    }
  },
}
</script>
